import React, { useMemo, memo, useCallback } from 'react';
import { Card, Button, Tooltip } from 'antd';
import { DownloadOutlined, InfoCircleOutlined } from '@ant-design/icons';
import {
  AreaChart,
  Area,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  TooltipProps,
} from 'recharts';
import { RevenueTrendData } from '../../services/dashboardService';
import {
  formatVnCalendarDate,
  formatVnChartDay,
  formatVnTime,
  vnDayjs,
} from '../../utils/vnDateTime';
import { formatVnd, formatVndCompact } from '../../utils/formatVnd';
import { DashboardCardHeader } from './DashboardCardHeader';
import { ChartSkeleton } from './ChartSkeleton';
import { DashboardEmptyState } from './DashboardEmptyState';

interface RevenueTrendChartProps {
  data: RevenueTrendData[];
  period?: 'today' | '7d' | '30d' | 'custom';
  loading?: boolean;
  variant?: 'default' | 'admin';
  rangeLabel?: string;
  startDate?: string;
  endDate?: string;
}

/** Fill missing dates in range for smooth chart display */
function fillMissingDates(
  items: RevenueTrendData[],
  startDate: string,
  endDate: string
): RevenueTrendData[] {
  if (!items || items.length === 0) return [];
  const byDate = new Map<string, RevenueTrendData>();
  items.forEach(item => {
    byDate.set(item.date, item);
  });
  const result: RevenueTrendData[] = [];
  const start = vnDayjs(startDate).startOf('day');
  const end = vnDayjs(endDate).endOf('day');
  for (
    let d = start;
    d.isSame(end, 'day') || d.isBefore(end, 'day');
    d = d.add(1, 'day')
  ) {
    const key = d.format('YYYY-MM-DD');
    result.push(byDate.get(key) ?? { date: key, revenue: 0, orders: 0 });
  }
  return result;
}

const RevenueTrendChart: React.FC<RevenueTrendChartProps> = ({
  data,
  period,
  loading = false,
  variant = 'default',
  rangeLabel = '',
  startDate: propStartDate,
  endDate: propEndDate,
}) => {
  const chartData = useMemo(() => {
    const raw = data || [];
    if (raw.length === 0) return [];
    const minDate =
      propStartDate ?? raw.reduce((a, b) => (a.date < b.date ? a : b)).date;
    const maxDate =
      propEndDate ?? raw.reduce((a, b) => (a.date > b.date ? a : b)).date;
    const filled = fillMissingDates(raw, minDate, maxDate);
    return filled.map(item => {
      const label =
        period === 'today'
          ? formatVnTime(item.date)
          : formatVnChartDay(item.date);
      return {
        ...item,
        label,
        dateValue: item.date,
        dateFormatted: formatVnCalendarDate(item.date, 'DD/MM/YYYY'),
      };
    });
  }, [data, period, propStartDate, propEndDate]);

  const insights = useMemo(() => {
    if (!data || data.length === 0) return null;
    const totalRevenue = data.reduce((s, d) => s + (d.revenue || 0), 0);
    const totalOrders = data.reduce((s, d) => s + (d.orders || 0), 0);
    const days = data.length;
    const avgPerDay = days > 0 ? totalRevenue / days : 0;
    const aov = totalOrders > 0 ? totalRevenue / totalOrders : 0;
    const best = data.reduce(
      (best, d) => ((d.revenue || 0) > (best.revenue || 0) ? d : best),
      data[0]
    );
    return {
      total: totalRevenue,
      totalOrders,
      avgPerDay,
      aov,
      bestDay: best ? { date: best.date, revenue: best.revenue || 0 } : null,
    };
  }, [data]);

  const handleExportCsv = useCallback(() => {
    const rows = data || [];
    const headers = ['Date', 'Revenue (VND)', 'Orders'];
    const lines = [
      headers.join(','),
      ...rows.map(r =>
        [r.date, (r.revenue || 0).toString(), (r.orders || 0).toString()].join(
          ','
        )
      ),
    ];
    const blob = new Blob([lines.join('\n')], {
      type: 'text/csv;charset=utf-8;',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `revenue-trend-${vnDayjs().format('YYYY-MM-DD')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [data]);

  const renderTooltip = useCallback(
    (props: TooltipProps<number, string>) => {
      const { active, payload, label } = props;
      if (!active || !payload?.length) return null;
      const item = chartData.find(d => d.label === label);
      if (!item) return null;
      const idx = chartData.findIndex(d => d.label === label);
      const prev = idx > 0 ? chartData[idx - 1] : null;
      const delta =
        prev && item.revenue !== undefined
          ? item.revenue - (prev.revenue || 0)
          : null;
      return (
        <div className="revenue-tooltip">
          <div className="revenue-tooltip-row">
            <strong>{item.dateFormatted}</strong>
          </div>
          <div className="revenue-tooltip-row">
            Doanh thu: {formatVnd(item.revenue || 0)}
          </div>
          <div className="revenue-tooltip-row">
            Đơn hàng: {(item.orders ?? 0).toLocaleString('vi-VN')}
          </div>
          {delta !== null && (
            <div
              className={`revenue-tooltip-row revenue-tooltip-delta ${
                delta >= 0 ? 'positive' : 'negative'
              }`}
            >
              {delta >= 0 ? '+' : ''}
              {formatVnd(delta)} so với trước
            </div>
          )}
        </div>
      );
    },
    [chartData]
  );

  if (loading) {
    return (
      <Card className="admin-revenue-card">
        <DashboardCardHeader
          title="Revenue Trends"
          subtitle={rangeLabel}
          infoTooltip="Tổng doanh thu theo ngày/tuần trong khoảng thời gian đã chọn."
        />
        <ChartSkeleton height={320} />
      </Card>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Card className="admin-revenue-card">
        <DashboardCardHeader
          title="Revenue Trends"
          subtitle={rangeLabel}
          infoTooltip="Tổng doanh thu theo ngày trong khoảng thời gian đã chọn."
        />
        <DashboardEmptyState
          message="Chưa có dữ liệu doanh thu trong khoảng thời gian đã chọn."
          suggestion="Thử mở rộng phạm vi ngày hoặc chọn khoảng khác."
        />
      </Card>
    );
  }

  return (
    <Card className="admin-revenue-card admin-revenue-card-enhanced">
      <DashboardCardHeader
        title="Revenue Trends"
        subtitle={rangeLabel}
        infoTooltip="GMV theo ngày trong khoảng thời gian đã chọn (đơn chưa hủy). Biểu đồ thể hiện xu hướng doanh thu và số đơn."
        actions={
          <div
            className="revenue-header-actions"
            style={{ display: 'flex', gap: 8, alignItems: 'center' }}
          >
            {/* <Tooltip title="Xuất dữ liệu ra file CSV">
              <Button
                type="default"
                size="small"
                icon={<DownloadOutlined />}
                onClick={handleExportCsv}
              >
                Export CSV
              </Button>
            </Tooltip> */}
            <Tooltip title="GMV theo ngày — tổng giá trị đơn chưa hủy trong khoảng thời gian đã chọn.">
              <InfoCircleOutlined style={{ color: '#8c8c8c', fontSize: 14 }} />
            </Tooltip>
          </div>
        }
      />
      {insights && (
        <div className="revenue-insight-row">
          <div className="revenue-insight-item">
            <span className="revenue-insight-label">GMV</span>
            <span className="revenue-insight-value">
              {formatVnd(insights.total)}
            </span>
          </div>
          <div className="revenue-insight-item">
            <span className="revenue-insight-label">Đơn hàng</span>
            <span className="revenue-insight-value">
              {insights.totalOrders.toLocaleString('vi-VN')}
            </span>
          </div>
          <div className="revenue-insight-item">
            <span className="revenue-insight-label">AOV</span>
            <span className="revenue-insight-value">
              {formatVnd(insights.aov)}
            </span>
          </div>
          <div className="revenue-insight-item">
            <span className="revenue-insight-label">TB/ngày</span>
            <span className="revenue-insight-value">
              {formatVnd(insights.avgPerDay)}
            </span>
          </div>
          {insights.bestDay && (
            <div className="revenue-insight-item">
              <span className="revenue-insight-label">Ngày cao nhất</span>
              <span className="revenue-insight-value">
                {formatVndCompact(insights.bestDay.revenue)} (
                {formatVnChartDay(insights.bestDay.date)})
              </span>
            </div>
          )}
        </div>
      )}
      <div className="revenue-chart-wrap">
        <ResponsiveContainer width="100%" height={320}>
          <AreaChart
            data={chartData}
            margin={{ top: 16, right: 24, left: 8, bottom: 8 }}
          >
            <defs>
              <linearGradient id="colorRevenuePro" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#2563eb" stopOpacity={0.3} />
                <stop offset="95%" stopColor="#2563eb" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid
              strokeDasharray="3 3"
              stroke="#e5e7eb"
              vertical={false}
            />
            <XAxis
              dataKey="label"
              tick={{ fontSize: 11, fill: '#6b7280' }}
              axisLine={{ stroke: '#e5e7eb' }}
              tickLine={false}
              height={40}
              label={{
                value: 'Ngày',
                position: 'insideBottom',
                offset: -8,
                style: { fill: '#9ca3af', fontSize: 11 },
              }}
            />
            <YAxis
              yAxisId="left"
              tick={{ fontSize: 11, fill: '#6b7280' }}
              tickFormatter={v => formatVndCompact(v)}
              axisLine={false}
              tickLine={false}
              width={72}
              label={{
                value: 'Doanh thu',
                angle: -90,
                position: 'insideLeft',
                style: { fill: '#9ca3af', fontSize: 11 },
              }}
            />
            <YAxis
              yAxisId="right"
              orientation="right"
              tick={{ fontSize: 11, fill: '#10b981' }}
              axisLine={false}
              tickLine={false}
              width={48}
              tickFormatter={v =>
                v >= 1000 ? `${(v / 1000).toFixed(1)}k` : String(v)
              }
            />
            <RechartsTooltip content={renderTooltip} />
            <Area
              yAxisId="left"
              type="monotone"
              dataKey="revenue"
              stroke="#2563eb"
              strokeWidth={2}
              fill="url(#colorRevenuePro)"
              isAnimationActive={true}
              animationDuration={400}
            />
            <Line
              yAxisId="right"
              type="monotone"
              dataKey="orders"
              stroke="#10b981"
              strokeWidth={2}
              dot={false}
              name="Đơn hàng"
              isAnimationActive={true}
              animationDuration={400}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </Card>
  );
};

export default memo(RevenueTrendChart);
